/** Pure platform helpers for PWA install / Android WebAPK repair detection. */

export function isInStandaloneMode(
  win: Pick<Window, 'matchMedia'> & { navigator: Navigator } = window,
): boolean {
  return (
    win.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in win.navigator &&
      (win.navigator as Navigator & { standalone?: boolean }).standalone === true)
  );
}

export function isIosDevice(nav: Navigator = navigator): boolean {
  return (
    /iphone|ipad|ipod/i.test(nav.userAgent) &&
    !('MSStream' in (globalThis as { MSStream?: unknown }))
  );
}

export function isAndroidDevice(nav: Navigator = navigator): boolean {
  return /android/i.test(nav.userAgent);
}

/**
 * True when Chrome reports our self-related webapp as installed, but this
 * document is not running in standalone display mode — typical when a user
 * falls back to a browser tab because the Android WebAPK icon won't launch
 * ("requires Chrome" despite Chrome being installed).
 */
export function shouldOfferAndroidPwaRepair(options: {
  isAndroid: boolean;
  isStandalone: boolean;
  relatedWebAppInstalled: boolean;
}): boolean {
  return options.isAndroid && !options.isStandalone && options.relatedWebAppInstalled;
}

export async function probeRelatedWebAppInstalled(
  nav: Navigator = navigator,
): Promise<boolean> {
  const getInstalled = (
    nav as Navigator & {
      getInstalledRelatedApps?: () => Promise<Array<{ platform?: string }>>;
    }
  ).getInstalledRelatedApps;
  if (typeof getInstalled !== 'function') return false;
  try {
    const apps = await getInstalled.call(nav);
    return apps.some((app) => app.platform === 'webapp');
  } catch {
    return false;
  }
}
